import React, { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../contexts/Provider";
import { send } from "../contexts/actions/form/send";
import FormTypeSwitcher from "./FormTypeSwitcher";
import {
    checkboxChecked,
    clearErrors,
    emailValid,
    markError,
    required,
    validateEmail
} from "../contexts/actions/form/validate";
import StoreSwitcher from "./StoreSwitcher";
import FormMessage from "./FormMessage";

window.privacyUrl = window.privacyUrl || '';

const validatePhone = (phone) => {
    if (!phone) return false;
    const phoneRegex = /^[\+]?[0-9\s\-]{9,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
};

const ContactForm = (props) => {
    const showProductSpecific = !!(props?.props?.categoryId || props?.props?.productId);
    const isSubsidy = props?.props?.isSubsidy === '1';

    const {
        state: {
            formType,
            store,
            productId,
            sent,
            sending,
            name: gName,
            email: gEmail,
            phone: gPhone,
            city: gCity,
            message: gMessage,
        },
        dispatch
    } = useContext(GlobalContext);

    const [filesOnUpload, setFilesOnUpload] = useState([]);

    const contactForm = useRef(null);
    const name = useRef(null);
    const city = useRef(null);
    const files = useRef(null);
    const fileLabel = useRef(null);
    const phone = useRef(null);
    const email = useRef(null);
    const message = useRef(null);
    const gdpr = useRef(null);

    useEffect(() => {
        if (sent && formType === 'contact') {
            let title = document.querySelector('.title-product-handle');
            if (title) {
                let titleProduct = title.textContent;
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: 'formSubmission',
                    formProduct: titleProduct,
                    phone: phone?.current?.value ?? "",
                    email: email.current.value,
                });
            } else {
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: 'formSubmission',
                    formProduct: store.title,
                    phone: phone?.current?.value ?? "",
                    email: email.current.value,
                });
            }
            contactForm.current.reset();
            setFilesOnUpload([]);
        }
    }, [sent]);

    useEffect(() => {
        const label = fileLabel.current;
        if (!label) return;

        label.addEventListener('dragenter', handleDragIn);
        label.addEventListener('dragleave', handleDragOut);
        label.addEventListener('dragover', handleDrag);
        label.addEventListener('drop', handleDrop);

        return () => {
            label.removeEventListener('dragenter', handleDragIn);
            label.removeEventListener('dragleave', handleDragOut);
            label.removeEventListener('dragover', handleDrag);
            label.removeEventListener('drop', handleDrop);
        };
    }, [showProductSpecific]);

    const validFile = () => {
        const el = files.current;
        const hasFile = !!(el?.files && el.files.length > 0);
        if (!hasFile) {
            el?.parentElement?.classList.add('error');
            return false;
        }
        el?.parentElement?.classList.remove('error');
        return true;
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }
    const handleDragIn = (e) => {
        e.preventDefault();
        e.stopPropagation();
        files.current.parentElement.classList.add('drag');
    }
    const handleDragOut = (e) => {
        e.preventDefault();
        e.stopPropagation();
        files.current.parentElement.classList.remove('drag');
    }
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        files.current.parentElement.classList.remove('drag');

        const dropped = e.dataTransfer?.files;
        if (dropped && dropped.length > 0) {
            const dt = new DataTransfer();
            Array.from(dropped).forEach(f => dt.items.add(f));
            files.current.files = dt.files;
            setFilesOnUpload([...dt.files]);
            validFile();
        }
    };

    const handleChangeFileInput = () => {
        setFilesOnUpload([...files.current.files]);
        validFile();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (window.grecaptcha === undefined) {
            window.location.reload();
        }

        clearErrors([name, email, message, phone, city, gdpr], { reflow: true });

        let valid = true;
        const firstErrorRefs = [];

        if (!required(name)) {
            markError(name);
            valid = false;
            firstErrorRefs.push(name);
        }
        if (!emailValid(email, validateEmail)) {
            markError(email);
            valid = false;
            firstErrorRefs.push(email);
        }
        if (!required(message)) {
            markError(message);
            valid = false;
            firstErrorRefs.push(message);
        }
        if (phone.current?.value && !validatePhone(phone.current.value)) {
            markError(phone);
            valid = false;
            firstErrorRefs.push(phone);
        }
        if (showProductSpecific && !required(city)) {
            markError(city);
            valid = false;
            firstErrorRefs.push(city);
        }
        if (!checkboxChecked(gdpr)) {
            markError(gdpr);
            valid = false;
            firstErrorRefs.push(gdpr);
        }
        if (showProductSpecific && !validFile()) {
            markError(files);
            valid = false;
            firstErrorRefs.push(files);
        }

        if (!valid) {
            const first = firstErrorRefs[0];
            if (first) markError(first, { focus: true, scroll: true });
            return;
        }

        if (valid) {
            const formData = new FormData(contactForm.current);
            formData.append('store_id', store.id);
            if (showProductSpecific) {
                formData.append('product_id', productId);
            }
            if (isSubsidy) {
                formData.append('is_subsidy', '1');
            }

            // Try reCAPTCHA, fallback to bypass on error
            try {
                if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
                    await window.grecaptcha.ready(async function () {
                        try {
                            const token = await window.grecaptcha.execute(window.captchaSiteKey || '6LdPEQMpAAAAAFll-gXz9LEZSAvZ558tn4nWQ8c-', { action: 'submit' });
                            formData.append("g-recaptcha-response", token);
                        } catch (error) {
                            console.log('reCAPTCHA error, using bypass:', error);
                            formData.append("g-recaptcha-response", "localhost-bypass");
                        }

                        send({
                            formData,
                            url: '/api/contact-form'
                        })(dispatch)
                    });
                } else {
                    // Skip reCAPTCHA on localhost or when not loaded
                    formData.append("g-recaptcha-response", "localhost-bypass");
                    send({
                        formData,
                        url: '/api/contact-form'
                    })(dispatch)
                }
            } catch (error) {
                console.log('reCAPTCHA failed, using bypass:', error);
                formData.append("g-recaptcha-response", "localhost-bypass");
                send({
                    formData,
                    url: '/api/contact-form'
                })(dispatch)
            }
        }
    }

    return (
        <div className={formType === "contact" ? "block" : "hidden"}>
            <form className={`w-full space-y-6 text-white product-form-placeholder ${sent ? 'hidden' : 'block'}`}
                  method="post"
                  onSubmit={handleSubmit}
                  ref={contactForm}
                  encType="multipart/form-data">

                <div className="relative floating-label">
                    <input
                        ref={name}
                        value={gName}
                        className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                        name="name"
                        type="text"
                        placeholder=" "
                        onChange={(e) => dispatch({
                            type: "SET_FIELD",
                            payload: { key: 'name', value: e.target.value }
                        })}
                    />
                    <label className="text-neutrals-400">Vor- und Nachname*</label>
                </div>

                <div className="relative floating-label">
                    <input
                        ref={email}
                        value={gEmail}
                        className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                        name="email"
                        type="text"
                        placeholder=" "
                        onChange={(e) => dispatch({
                            type: "SET_FIELD",
                            payload: { key: "email", value: e.target.value }
                        })}
                    />
                    <label className="text-neutrals-400">E-Mail*</label>
                </div>

                <div className="relative floating-label">
                    <input
                        ref={phone}
                        value={gPhone}
                        name="phone"
                        className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                        type="tel"
                        placeholder=" "
                        onChange={(e) => dispatch({
                            type: "SET_FIELD",
                            payload: { key: "phone", value: e.target.value }
                        })}
                    />
                    <label className="text-neutrals-400">Telefon</label>
                </div>

                {showProductSpecific && (
                    <div className="relative floating-label">
                        <input
                            ref={city}
                            value={gCity}
                            name="city"
                            className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                            type="text"
                            placeholder=" "
                            onChange={(e) => dispatch({
                                type: "SET_FIELD",
                                payload: { key: "city", value: e.target.value }
                            })}
                        />
                        <label className="text-neutrals-400">Montageort*</label>
                    </div>
                )}

                <StoreSwitcher />

                {/* Meeting booking disabled for AT version */}
                {/* <FormTypeSwitcher /> */}

                <div className="relative floating-label">
                    <textarea
                        name="message"
                        value={gMessage}
                        ref={message}
                        rows={5}
                        className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                        placeholder=" "
                        onChange={(e) => dispatch({
                            type: "SET_FIELD",
                            payload: { key: "message", value: e.target.value }
                        })}
                    />
                    <label className="text-neutrals-400">Nachricht*</label>
                </div>

                {showProductSpecific && (
                    <div className="">
                        <label
                            ref={fileLabel}
                            htmlFor="file"
                            className={
                                "!relative block w-full px-4 py-4 border-2 border-dashed border-neutrals-700 rounded flex items-center gap-3 cursor-pointer transition-colors " +
                                ((filesOnUpload[0] && filesOnUpload[0].name) ? "text-white" : "text-neutrals-400")
                            }
                        >
                            <img src="/images/icons/other/icon_upload.svg" alt=""
                                 className="w-5 h-5 shrink-0" />
                            <span className="truncate">
                                    {filesOnUpload[0]?.name || "Bild hochladen"}
                                </span>
                        </label>

                        <input
                            id="file"
                            ref={files}
                            name="file"
                            type="file"
                            className="hidden"
                            onChange={handleChangeFileInput}
                        />
                    </div>
                )}

                <div className="flex flex-col lg:flex-row items-start justify-between gap-4">
                    <div className="checkbox-container">
                        <label htmlFor="gdpr" className="flex items-start gap-3 cursor-pointer">
                            <input id="gdpr" ref={gdpr} type="checkbox" className="mt-1" />
                            <p className="text-12 text-neutrals-400 leading-snug letter-spacing-2per line-height-130">
                                Mit dem Absenden der Anfrage stimme ich der {" "}
                                <a target="_blank" href={window.privacyUrl} className="underline">
                                    Verarbeitung personenbezogener Daten
                                </a>{" "}
                                durch Profirol gemäß der Datenschutzrichtlinie zu.
                            </p>
                        </label>
                    </div>

                    <button type="submit"
                            className="form-btn self-start w-full lg:w-auto hover:bg-neutrals-500 transition duration-200 hover:text-white disabled:opacity-60"
                            disabled={sending}>
                        {sending ? "Wird gesendet..." : "Absenden"}
                    </button>
                </div>
            </form>
            { sent ? <FormMessage message="Vielen Dank für Ihre Nachricht, wir werden Sie bald kontaktieren." /> : null}
        </div>
    );
}

export default ContactForm;
